import React, { useState, useEffect, useRef } from 'react';
import { X, Plus, Edit2, Trash2, Save, ChevronLeft, Lock } from 'react-feather';
import Modal from './Modal';
import { useAuth } from '../contexts/AuthContext';
import { useToast } from '../contexts/ToastContext';
import { emailTemplatesAPI, EmailTemplate } from '../services/api';
import { useEmailVariables } from '../hooks/useEmailVariables';
import { QuickTags } from './QuickTags';
import { useResponsive } from '../hooks/useResponsive';
import { Field, TextInput, TextArea, fieldAria } from './ui';

interface EmailTemplatesModalProps {
  isOpen: boolean;
  onClose: () => void;
}

type ViewMode = 'list' | 'create' | 'edit';
type ScopeTab = 'global' | 'org' | 'personal';

const scopeBadgeColors: Record<string, { color: string; bg: string }> = {
  global: { color: '#6B7280', bg: '#F3F4F6' },
  org: { color: '#3B82F6', bg: '#DBEAFE' },
  personal: { color: '#10B981', bg: '#D1FAE5' },
};

const EmailTemplatesModal: React.FC<EmailTemplatesModalProps> = ({ isOpen, onClose }) => {
  const { sessionToken, user } = useAuth();
  const toast = useToast();
  const { isMobile } = useResponsive();
  const [templates, setTemplates] = useState<EmailTemplate[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [viewMode, setViewMode] = useState<ViewMode>('list');
  const [editingTemplate, setEditingTemplate] = useState<EmailTemplate | null>(null);
  const [deleteConfirmId, setDeleteConfirmId] = useState<string | null>(null);
  const [activeTab, setActiveTab] = useState<ScopeTab>('personal');

  const isStaff = user?.user_type === 'staff';
  const isSuperAdmin = user?.staff_role === 'super_admin' || false;
  const isOrgAdmin = user?.staff_role === 'org_admin' || false;
  const showScopeTabs = isStaff || isSuperAdmin;

  // Form state
  const [name, setName] = useState('');
  const [subject, setSubject] = useState('');
  const [body, setBody] = useState('');
  const [activeField, setActiveField] = useState<'subject' | 'body'>('body');
  const [cursorPosition, setCursorPosition] = useState(0);

  const subjectRef = useRef<HTMLInputElement>(null);
  const bodyRef = useRef<HTMLTextAreaElement>(null);

  // Get all available QuickTags for template editing
  const { tags } = useEmailVariables({
    context: {
      hasCoach: true,
      hasYouTube: true,
      isThreadReply: false,
      showAllTags: true
    }
  });

  // Load templates when modal opens
  useEffect(() => {
    if (isOpen && sessionToken) {
      loadTemplates();
    }
  }, [isOpen, sessionToken]);

  // Open on the list next time.
  useEffect(() => {
    if (!isOpen) {
      setViewMode('list');
      setEditingTemplate(null);
    }
  }, [isOpen]);

  // Reset form when switching views
  useEffect(() => {
    if (viewMode === 'list') {
      setName('');
      setSubject('');
      setBody('');
      setEditingTemplate(null);
    } else if (viewMode === 'edit' && editingTemplate) {
      setName(editingTemplate.name);
      setSubject(editingTemplate.subject || '');
      setBody(editingTemplate.body);
    }
  }, [viewMode, editingTemplate]);

  const loadTemplates = async () => {
    if (!sessionToken) return;
    setIsLoading(true);
    try {
      const response = await emailTemplatesAPI.getTemplates(sessionToken);
      if (response.success) {
        setTemplates(response.templates);
      }
    } catch (error) {
      console.error('Error loading templates:', error);
      toast.showToast('Failed to load templates', 'error');
    } finally {
      setIsLoading(false);
    }
  };

  const handleCreate = () => {
    setViewMode('create');
    setName('');
    setSubject('');
    setBody('');
  };

  const handleEdit = (template: EmailTemplate) => {
    setEditingTemplate(template);
    setViewMode('edit');
  };

  const handleBack = () => {
    setViewMode('list');
    setEditingTemplate(null);
  };

  const handleSave = async () => {
    if (!sessionToken) return;

    if (!name.trim()) {
      toast.showToast('Template name is required', 'error');
      return;
    }

    if (!body.trim()) {
      toast.showToast('Template body is required', 'error');
      return;
    }

    setIsSaving(true);
    try {
      const data: any = {
        name: name.trim(),
        subject: subject.trim() || undefined,
        body: body.trim()
      };

      // Add scope for new templates based on active tab
      if (viewMode === 'create' && showScopeTabs) {
        data.scope = activeTab;
        if (activeTab === 'org' && user?.organization_id) {
          data.org_id = user.organization_id;
        }
      }

      if (viewMode === 'create') {
        const response = await emailTemplatesAPI.createTemplate(sessionToken, data);
        if (response.success) {
          toast.showToast('Template saved.', 'success');
          onClose();
        } else {
          // #196: a success:false response used to give zero feedback.
          toast.error((response as any)?.message || 'Failed to create template. Please try again.');
        }
      } else if (viewMode === 'edit' && editingTemplate) {
        const response = await emailTemplatesAPI.updateTemplate(sessionToken, editingTemplate.id, data);
        if (response.success) {
          toast.showToast('Template saved.', 'success');
          onClose();
        } else {
          // #196: a success:false response used to give zero feedback.
          toast.error((response as any)?.message || 'Failed to update template. Please try again.');
        }
      }
    } catch (error) {
      console.error('Error saving template:', error);
      toast.showToast('Failed to save template', 'error');
    } finally {
      setIsSaving(false);
    }
  };

  const handleDelete = async (templateId: string) => {
    if (!sessionToken) return;

    try {
      const response = await emailTemplatesAPI.deleteTemplate(sessionToken, templateId);
      if (response.success) {
        toast.showToast('Template deleted successfully', 'success');
        setDeleteConfirmId(null);
        await loadTemplates();
      } else {
        // #196: a success:false response used to give zero feedback.
        toast.error((response as any)?.message || 'Failed to delete template. Please try again.');
      }
    } catch (error) {
      console.error('Error deleting template:', error);
      toast.showToast('Failed to delete template', 'error');
    }
  };

  const insertQuickTag = (tagValue: string) => {
    if (activeField === 'subject' && subjectRef.current) {
      const start = subjectRef.current.selectionStart || 0;
      const end = subjectRef.current.selectionEnd || 0;
      const newValue = subject.substring(0, start) + tagValue + subject.substring(end);
      setSubject(newValue);
      // Set cursor position after inserted tag
      setTimeout(() => {
        subjectRef.current?.setSelectionRange(start + tagValue.length, start + tagValue.length);
        subjectRef.current?.focus();
      }, 0);
    } else if (activeField === 'body' && bodyRef.current) {
      const start = bodyRef.current.selectionStart || 0;
      const end = bodyRef.current.selectionEnd || 0;
      const newValue = body.substring(0, start) + tagValue + body.substring(end);
      setBody(newValue);
      // Set cursor position after inserted tag
      setTimeout(() => {
        bodyRef.current?.setSelectionRange(start + tagValue.length, start + tagValue.length);
        bodyRef.current?.focus();
      }, 0);
    }
  };

  if (!isOpen) return null;

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      isDirty={viewMode !== 'list' && (name.trim().length > 0 || subject.trim().length > 0 || body.trim().length > 0)}
      disableClose={isSaving}
      ariaLabel="Email Templates"
      zIndex={10002}
      contentStyle={{
        backgroundColor: 'white',
        borderRadius: '16px',
        width: isMobile ? 'calc(100vw - 32px)' : '100%',
        maxWidth: isMobile ? 'calc(100vw - 32px)' : '700px',
        maxHeight: isMobile ? 'calc(100vh - 100px)' : '90vh',
        overflow: 'hidden',
        padding: 0,
        display: 'flex',
        flexDirection: 'column',
      }}
    >
        {/* Header */}
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            padding: isMobile ? '16px' : '20px 24px',
            borderBottom: '1px solid #E5E7EB'
          }}
        >
          <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
            {viewMode !== 'list' && (
              <button
                onClick={handleBack}
                style={{
                  background: 'none',
                  border: 'none',
                  cursor: 'pointer',
                  padding: '4px',
                  display: 'flex',
                  alignItems: 'center'
                }}
              >
                <ChevronLeft size={24} color="#6B7280" />
              </button>
            )}
            <h2 style={{ margin: 0, fontSize: '20px', fontWeight: '600', color: '#1F2937' }}>
              {viewMode === 'list' && 'Email Templates'}
              {viewMode === 'create' && 'Create Template'}
              {viewMode === 'edit' && 'Edit Template'}
            </h2>
          </div>
          <button
            onClick={onClose}
            style={{
              background: 'none',
              border: 'none',
              cursor: 'pointer',
              padding: '4px'
            }}
          >
            <X size={24} color="#6B7280" />
          </button>
        </div>

        {/* Content */}
        <div style={{ flex: 1, overflow: 'auto', padding: '24px' }}>
          {viewMode === 'list' ? (
            <>
              {/* Scope Tabs — only shown for staff/admin users */}
              {showScopeTabs && (
                <div style={{ display: 'flex', gap: '4px', marginBottom: '16px', borderBottom: '1px solid #E5E7EB', paddingBottom: '12px' }}>
                  {(['global', 'org', 'personal'] as ScopeTab[]).map((tab) => {
                    const tabLabel = tab === 'global' ? 'Global' : tab === 'org' ? (user?.org_name || 'Organization') : 'My Templates';
                    const count = templates.filter(t => (t.scope || 'personal') === tab).length;
                    return (
                      <button
                        key={tab}
                        onClick={() => setActiveTab(tab)}
                        style={{
                          padding: '8px 16px',
                          backgroundColor: activeTab === tab ? '#151133' : 'transparent',
                          color: activeTab === tab ? 'white' : '#6B7280',
                          border: 'none',
                          borderRadius: '6px',
                          fontSize: '14px',
                          fontWeight: activeTab === tab ? '600' : '500',
                          cursor: 'pointer',
                          transition: 'all 0.2s ease'
                        }}
                      >
                        {tabLabel} {count > 0 && <span style={{ marginLeft: '4px', fontSize: '12px', opacity: 0.7 }}>({count})</span>}
                      </button>
                    );
                  })}
                </div>
              )}

              {/* Create New Button — hidden if tab is read-only for this user */}
              {(activeTab === 'personal' || (activeTab === 'org' && (isOrgAdmin || isSuperAdmin)) || (activeTab === 'global' && isSuperAdmin) || !showScopeTabs) && (
              <button
                onClick={handleCreate}
                style={{
                  width: '100%',
                  padding: '16px',
                  backgroundColor: '#f9550d',
                  color: 'white',
                  border: 'none',
                  borderRadius: '8px',
                  fontSize: '16px',
                  fontWeight: '600',
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: '8px',
                  marginBottom: '24px'
                }}
              >
                <Plus size={20} />
                Create New Template
              </button>
              )}

              {/* Templates List — filtered by active scope tab */}
              {isLoading ? (
                <div style={{ textAlign: 'center', padding: '40px', color: '#6B7280' }}>
                  Loading templates...
                </div>
              ) : (() => {
                const filteredTemplates = showScopeTabs
                  ? templates.filter(t => (t.scope || 'personal') === activeTab)
                  : templates;
                return filteredTemplates.length === 0 ? (
                <div style={{ textAlign: 'center', padding: '40px', color: '#6B7280' }}>
                  <p style={{ margin: '0 0 8px 0', fontSize: '16px' }}>No templates yet</p>
                  <p style={{ margin: 0, fontSize: '14px' }}>
                    {activeTab === 'global' ? 'No global templates available' :
                     activeTab === 'org' ? 'No organization templates yet' :
                     'Create your first template to save time when emailing coaches'}
                  </p>
                </div>
              ) : (
                <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                  {filteredTemplates.map((template) => (
                    <div
                      key={template.id}
                      style={{
                        padding: '16px',
                        backgroundColor: '#F9FAFB',
                        borderRadius: '8px',
                        border: '1px solid #E5E7EB'
                      }}
                    >
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                        <div style={{ flex: 1, minWidth: 0 }}>
                          <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '4px' }}>
                            <h3 style={{ margin: 0, fontSize: '16px', fontWeight: '600', color: '#1F2937' }}>
                              {template.name}
                            </h3>
                            {showScopeTabs && template.scope && template.scope !== 'personal' && (
                              <span style={{
                                padding: '2px 8px',
                                borderRadius: '10px',
                                fontSize: '11px',
                                fontWeight: '600',
                                color: scopeBadgeColors[template.scope]?.color || '#6B7280',
                                backgroundColor: scopeBadgeColors[template.scope]?.bg || '#F3F4F6',
                              }}>
                                {template.scope === 'global' ? 'Global' : template.org_name || 'Org'}
                              </span>
                            )}
                            {template.is_editable === false && (
                              <Lock size={14} color="#9CA3AF" />
                            )}
                          </div>
                          {template.subject && (
                            <p style={{ margin: '0 0 8px 0', fontSize: '14px', color: '#6B7280' }}>
                              Subject: {template.subject}
                            </p>
                          )}
                          <p
                            style={{
                              margin: 0,
                              fontSize: '14px',
                              color: '#6B7280',
                              overflow: 'hidden',
                              textOverflow: 'ellipsis',
                              whiteSpace: 'nowrap'
                            }}
                          >
                            {template.body.substring(0, 100)}...
                          </p>
                        </div>
                        <div style={{ display: 'flex', gap: '8px', marginLeft: '16px' }}>
                          {template.is_editable !== false && (
                          <>
                          <button
                            onClick={() => handleEdit(template)}
                            style={{
                              padding: '8px',
                              backgroundColor: 'white',
                              border: '1px solid #D1D5DB',
                              borderRadius: '6px',
                              cursor: 'pointer',
                              display: 'flex',
                              alignItems: 'center'
                            }}
                          >
                            <Edit2 size={16} color="#6B7280" />
                          </button>
                          {deleteConfirmId === template.id ? (
                            <div style={{ display: 'flex', gap: '4px' }}>
                              <button
                                onClick={() => handleDelete(template.id)}
                                style={{
                                  padding: '8px 12px',
                                  backgroundColor: '#EF4444',
                                  color: 'white',
                                  border: 'none',
                                  borderRadius: '6px',
                                  cursor: 'pointer',
                                  fontSize: '12px',
                                  fontWeight: '500'
                                }}
                              >
                                Confirm
                              </button>
                              <button
                                onClick={() => setDeleteConfirmId(null)}
                                style={{
                                  padding: '8px 12px',
                                  backgroundColor: '#F3F4F6',
                                  color: '#374151',
                                  border: 'none',
                                  borderRadius: '6px',
                                  cursor: 'pointer',
                                  fontSize: '12px',
                                  fontWeight: '500'
                                }}
                              >
                                Cancel
                              </button>
                            </div>
                          ) : (
                            <button
                              onClick={() => setDeleteConfirmId(template.id)}
                              style={{
                                padding: '8px',
                                backgroundColor: 'white',
                                border: '1px solid #D1D5DB',
                                borderRadius: '6px',
                                cursor: 'pointer',
                                display: 'flex',
                                alignItems: 'center'
                              }}
                            >
                              <Trash2 size={16} color="#EF4444" />
                            </button>
                          )}
                          </>
                          )}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              );
              })()}
            </>
          ) : (
            /* Create/Edit Form */
            <div style={{ display: 'flex', flexDirection: 'column', gap: '20px' }}>
              {/* Template Name */}
              <Field id="template-name" label="Template Name" required>
                <TextInput
                  {...fieldAria('template-name')}
                  type="text"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  placeholder="e.g., Initial Introduction"
                  style={{
                    padding: '12px 16px',
                    border: '1px solid #D1D5DB',
                    borderRadius: '8px',
                    fontSize: '14px',
                  }}
                />
              </Field>

              {/* Subject */}
              <Field id="template-subject" label="Subject Line">
                <TextInput
                  {...fieldAria('template-subject')}
                  ref={subjectRef}
                  type="text"
                  value={subject}
                  onChange={(e) => setSubject(e.target.value)}
                  onFocus={() => setActiveField('subject')}
                  placeholder="e.g., {{athlete.firstName}} - {{athlete.graduationYear}} {{athlete.position}}"
                  style={{
                    padding: '12px 16px',
                    border: '1px solid #D1D5DB',
                    borderRadius: '8px',
                    fontSize: '14px',
                  }}
                />
              </Field>

              {/* Body */}
              <Field id="template-body" label="Message Body" required>
                <TextArea
                  {...fieldAria('template-body')}
                  ref={bodyRef}
                  value={body}
                  onChange={(e) => setBody(e.target.value)}
                  onFocus={() => setActiveField('body')}
                  placeholder="Write your template message here. Use the tags below to insert dynamic content..."
                  style={{
                    height: '200px',
                    padding: '12px 16px',
                    border: '1px solid #D1D5DB',
                    borderRadius: '8px',
                    fontSize: '14px',
                  }}
                />
              </Field>

              {/* QuickTags */}
              <div>
                <p style={{ fontSize: '14px', color: '#6B7280', margin: '0 0 8px 0' }}>
                  Click a tag to insert it at your cursor position in the{' '}
                  <strong>{activeField === 'subject' ? 'subject' : 'body'}</strong> field:
                </p>
                <QuickTags
                  tags={tags}
                  onInsertTag={(tag) => insertQuickTag(tag.value)}
                  disabled={isSaving}
                />
              </div>

              {/* Save Button */}
              <button
                onClick={handleSave}
                disabled={isSaving}
                style={{
                  width: '100%',
                  padding: '14px',
                  backgroundColor: isSaving ? '#9CA3AF' : '#f9550d',
                  color: 'white',
                  border: 'none',
                  borderRadius: '8px',
                  fontSize: '16px',
                  fontWeight: '600',
                  cursor: isSaving ? 'not-allowed' : 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: '8px',
                  marginTop: '8px'
                }}
              >
                <Save size={20} />
                {isSaving ? 'Saving...' : viewMode === 'create' ? 'Create Template' : 'Save Changes'}
              </button>
            </div>
          )}
        </div>
      </Modal>
  );
};

export default EmailTemplatesModal;
